import { useEffect, useMemo, useState, type ReactElement } from 'react';

import { useTranslation } from '@govard/i18n-core/react';
import { Bell, Check } from 'lucide-react';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { NotificationType } from './types.js';

export interface NotificationItem {
  id: string;
  title: string;
  body?: string;
  type?: NotificationType;
  read?: boolean;
  createdAt?: string;
}

export interface NotificationCenterWidgetProps {
  notifications?: readonly NotificationItem[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onMarkAllRead?: (ids: readonly string[]) => Promise<void> | void;
  onItemClick?: (notification: NotificationItem) => void;
}

const TYPE_COLORS: Record<string, string> = {
  info: 'var(--color-info, #2563eb)',
  success: 'var(--color-success, #16a34a)',
  warning: 'var(--color-warning, #d97706)',
  error: 'var(--color-error, #dc2626)',
  approval: '#8b5cf6',
};

function timeAgo(timestamp: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) {
    return t('crm.dashboard.time.just_now');
  }
  if (minutes < 60) {
    return t('crm.dashboard.time.minutes_ago', { count: minutes });
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t('crm.dashboard.time.hours_ago', { count: hours });
  }
  return t('crm.dashboard.time.days_ago', { count: Math.floor(hours / 24) });
}

export function NotificationCenterWidget({
  notifications = [],
  className,
  testId,
  loading = false,
  onMarkAllRead,
  onItemClick,
}: NotificationCenterWidgetProps): ReactElement {
  const { t } = useTranslation();
  const [localNotifications, setLocalNotifications] = useState<NotificationItem[]>(() => [...notifications]);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  useEffect(() => {
    setLocalNotifications([...notifications]);
  }, [notifications]);

  const unreadIds = useMemo(
    () => localNotifications.filter((notification) => !notification.read).map((notification) => notification.id),
    [localNotifications],
  );

  async function handleMarkAllRead(): Promise<void> {
    if (unreadIds.length === 0 || !onMarkAllRead || isMarkingAllRead) {
      return;
    }

    setIsMarkingAllRead(true);

    try {
      await onMarkAllRead(unreadIds);
      setLocalNotifications((current) => current.map((notification) => ({ ...notification, read: true })));
    } finally {
      setIsMarkingAllRead(false);
    }
  }

  const widgetContainerProps = {
    title: t('crm.dashboard.notifications.title'),
    icon: Bell,
    ...(unreadIds.length > 0 ? { iconColor: 'var(--color-warning, #d97706)' } : {}),
    minHeight: 260,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <div className="flex items-center" style={{ gap: 8 }}>
        {unreadIds.length > 0 ? (
          <>
            <span
              className="font-bold rounded-full flex items-center justify-center"
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 10,
                width: 18,
                height: 18,
                backgroundColor: 'var(--color-warning, #d97706)',
                color: '#fff',
              }}
            >
                {unreadIds.length}
              </span>
              <button
                type="button"
                onClick={handleMarkAllRead}
                disabled={!onMarkAllRead || isMarkingAllRead}
                className="flex items-center"
                style={{ fontSize: 10, color: onMarkAllRead ? 'var(--color-primary, #c47322)' : 'var(--color-text-muted, #8a7f70)', gap: 3, fontWeight: 500, cursor: onMarkAllRead ? 'pointer' : 'default', opacity: !onMarkAllRead || isMarkingAllRead ? 0.6 : 1 }}
              >
                <Check size={10} /> {t('crm.dashboard.notifications.mark_all_read')}
              </button>
          </>
        ) : null}
      </div>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1, 2, 3].map((index) => (
            <div key={index} className="rounded" style={{ height: 40, background: 'var(--color-border, #ddd7cc)', opacity: 0.3 }} />
          ))}
        </div>
      ) : localNotifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full" style={{ opacity: 0.5, padding: 24 }}>
          <Bell size={28} style={{ color: 'var(--color-text-muted, #8a7f70)', marginBottom: 8 }} />
          <p style={{ fontSize: 12, color: 'var(--color-text-muted, #8a7f70)' }}>{t('crm.dashboard.notifications.empty')}</p>
        </div>
      ) : (
        <div className="flex flex-col" style={{ gap: 2 }}>
          {localNotifications.map((notification) => {
            const color = TYPE_COLORS[notification.type ?? 'info'] ?? 'var(--color-text-muted, #8a7f70)';

            return (
              <button
                key={notification.id}
                type="button"
                {...(onItemClick ? { onClick: () => onItemClick(notification) } : {})}
                disabled={!onItemClick}
                className="flex w-full items-start rounded-lg text-left"
                style={{
                  padding: '8px 10px',
                  gap: 10,
                  opacity: notification.read ? 0.6 : onItemClick ? 1 : 0.9,
                  backgroundColor: notification.read ? 'transparent' : 'rgba(139, 92, 246, 0.03)',
                  cursor: onItemClick ? 'pointer' : 'default',
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: notification.read ? 'transparent' : color,
                    marginTop: 5,
                    flexShrink: 0,
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate" style={{ fontSize: 12, color: 'var(--color-text, #2d2418)' }}>
                    {notification.title}
                  </div>
                  {notification.body ? (
                    <div className="truncate" style={{ fontSize: 10, color: 'var(--color-text-muted, #8a7f70)', marginTop: 2 }}>
                      {notification.body}
                    </div>
                  ) : null}
                </div>
                <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 9, color: 'var(--color-text-muted, #8a7f70)', marginTop: 3 }}>
                  {notification.createdAt ? timeAgo(notification.createdAt, t) : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </WidgetContainer>
  );
}
